import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  IconButton,
  lighten,
  Stack,
  Typography,
} from '@mui/material';
import { Eye as EyeIcon, UserPlus as UserPlusIcon, UsersThree as UsersThreeIcon } from '@phosphor-icons/react';
import {
  MaterialReactTable,
  MRT_FilterCheckbox,
  MRT_FilterTextField,
  MRT_GlobalFilterTextField,
  MRT_RowData,
  MRT_Table,
  MRT_TableContainer,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { UserTableTile } from '@/components/core/v2/user-table-tile';

import { CreateManagerModal } from './create-manager-modal';
import { AssignOrCreateManagerModal } from './assign-or-create-manager-modal';

interface ManagersTableProps {
  rows: User[];
  loading: boolean;
  onCreated: (user: User) => void;
}

export function ManagersTable({ rows, loading, onCreated }: ManagersTableProps): React.JSX.Element {
  const dialog = useDialog();
  const navigate = useNavigate();
  const { community } = useUser();
  const [openAssign, setOpenAssign] = React.useState(false);
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 150,
        accessorFn: (row) => {
          return <UserTableTile user={row} />;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 150,
      },
    ],
    []
  );

  const handleSuccess = (user: User) => {
    onCreated(user);
    dialog.handleClose();
  };

  const table = useMaterialReactTable<User>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={() => {
          navigate(paths.users.details(row.original.id));
        }}
        size="small"
      >
        <EyeIcon size={20} />
      </IconButton>
    ),

    renderTopToolbar: ({ table }) => {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
          })}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* import MRT sub-components */}
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
          </Box>
        
        </Box>
      );
    },
  });

  return (
    <React.Fragment>
      <Card>
        <Box 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <UsersThreeIcon size={24} style={{ marginRight: '8px', color: 'var(--mui-palette-primary-main)' }} />
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle1">
                {rows.length} {rows.length === 1 ? 'Manager' : 'Managers'}
              </Typography>
            </Box>
          </Stack>
          
          <Button
            onClick={() => { setOpenAssign(true); }}
            size="small"
            startIcon={<UserPlusIcon size={18} />}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            variant="contained"
          >
            Assign or Create Manager
          </Button>
        </Box>

        <MaterialReactTable table={table} />
      </Card>
      <AssignOrCreateManagerModal
        communityId={community?.id ?? ''}
        onClose={() => { setOpenAssign(false); }}
        onCreateNew={() => {
          setOpenAssign(false);
          dialog.handleOpen();
        }}
        onSuccess={handleSuccess}
        open={openAssign}
      />
      <CreateManagerModal
        communityId={community?.id ?? ''}
        onClose={dialog.handleClose}
        onSuccess={handleSuccess}
        open={dialog.open}
      />
    </React.Fragment>
  );
}
